import { useState } from "react";
import { Nav, NavItem, NavLink, TabContent } from "reactstrap";
import { BusinessHours, BusinessTimeZone, dayJsTimeFormat, getTimeSpans, HoursSettings, TimeSpan, validateHours, ValidationError } from "../../../common/BusinessHours";
import useDisplayMessage from "../../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../../common/lib/KaAlert";
import QueryView from "../../../common/lib/QueryView";
import Row from "../../../common/Row";
import SpinnerButton from "../../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../../common/userFacingMessages/userFacingMessages";
import { getDisabledStyle } from "../../../common/utilities";
import { DayOfTheWeek } from "../../../requests/dtos/Hours";
import useGetSiteSettings from "../../../requests/useGetSiteSettings";
import useUpdateBusinessHours from "../../../requests/useUpdateBusinessHours";
import BusinessHoursDayEntry from "./BusinessHoursEntry";

enum Direction {
  "Loadout",
  "Receiving"
}

type BusinessHoursSettings = {
  businessHours: BusinessHours;
}

type NavHeaderProps = {
  enabled: boolean;
  active: boolean;
  inError: boolean;
  text: string;
  onClick: () => void;
}

const NavHeader = ({ enabled, active, inError, text, onClick }: NavHeaderProps) => {
  return <NavLink
    disabled={!enabled}
    className={
      `px-5 
      ${enabled && inError && 'text-danger'} 
      ${enabled && inError && active && 'border-danger'}`}
    active={enabled && active}
    onClick={onClick}>
    {text}
  </NavLink>
}

const Edit = ({ initialBusinessHours }: { initialBusinessHours: BusinessHours }) => {
  const displayMessage = useDisplayMessage();

  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    ...initialBusinessHours,
    loadoutHours: initialBusinessHours.loadoutHours,
    receivingHours: initialBusinessHours.receivingHours,
  });

  const [loadoutErrors, setLoadoutErrors] = useState<ValidationError>({});
  const [receivingErrors, setReceivingErrors] = useState<ValidationError>({});

  const [viewReceiving, setViewReceiving] = useState(false);

  const businessHoursEnabled = businessHours.useBusinessHours;

  const toggleReceiving = () => {
    const useLoadoutForBoth = businessHours.useLoadoutHoursForReceiving;
    updateSettings({ useLoadoutHoursForReceiving: !useLoadoutForBoth });
    if (useLoadoutForBoth) setViewReceiving(false);
  }

  const updateBusinessHoursSettings = useUpdateBusinessHours({
    onSuccess: () => displayMessage.success("Business Hours updated"),
    onError: err => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const getHours = (direction: Direction) =>
    direction === Direction.Loadout ? businessHours.loadoutHours : businessHours.receivingHours;

  const save = async () => {
    const businessHoursForSave = {
      ...businessHours,
      loadoutHours: prepareForSave(businessHours.loadoutHours),
      receivingHours: prepareForSave(businessHours.receivingHours)
    }
      
    await updateBusinessHoursSettings.request(businessHoursForSave);
  }

  const prepareForSave = (hours: HoursSettings): HoursSettings => {
    const result: HoursSettings = {} as HoursSettings;

    for (const day in hours) {
      const intervals = hours[day as keyof HoursSettings];
      result[day as keyof HoursSettings] = intervals.filter(
        (interval) => !!interval.openTime && !!interval.closeTime
      );
    }

    return result;
  }

  const updateSettings = (update: Partial<BusinessHours>) => {
    setBusinessHours({
      ...businessHours,
      ...update,
      loadoutHours: { ...businessHours.loadoutHours, ...update.loadoutHours },
      receivingHours: { ...businessHours.receivingHours, ...update.receivingHours },
    });
  };

  const changeTimeZone = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) =>
    setBusinessHours({
      ...businessHours,
      timeZone: BusinessTimeZone[value as keyof typeof BusinessTimeZone]
    });

  const updateAndValidateHours = (
    direction: Direction,
    day: DayOfTheWeek,
    timeSpans: TimeSpan[] | null
  ) => {
    const hours = {
      ...getHours(direction),
      [day]: timeSpans?.map(({ openTime, closeTime }) => ({
        openTime: openTime ? openTime.format(dayJsTimeFormat) : null,
        closeTime: closeTime ? closeTime.format(dayJsTimeFormat) : null,
      })) ?? []
    };

    const isLoadout = direction === Direction.Loadout;

    updateSettings(isLoadout
      ? { loadoutHours: hours }
      : { receivingHours: hours }
    );

    const validatedErrors = validateHours(hours);
    isLoadout
      ? setLoadoutErrors(validatedErrors)
      : setReceivingErrors(validatedErrors);
  };

  const noErrorsForSaving = (direction: Direction) => {
    const errors = direction === Direction.Loadout ? loadoutErrors : receivingErrors;
    return Object.values(errors).every(dayErrors => 
      dayErrors ? dayErrors.every(error => !error.openTimeError && !error.closeTimeError) : true
    );
  };

  return <>
    <div className="m-auto w-65">
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <Row>
        <h2>Business Hours</h2>
        <p>
          Drivers can view business hours within the LOADPASS application on the 'Where are
          you loading?' screen. These hours can be adjusted before holidays to communicate any
          changes to the current operating schedule.
        </p>
        <p>
          For sites that operate past midnight, overnight hours can be set to reflect open and close
          times that extend into the next day. For example, if a site is open from 9:00 PM on Tuesday
          to 3:00 AM on Wednesday, admins can add hours to Tuesday with an open time of 9:00 PM and
          a close time of 3:00 AM. For overnight hours, the close time is limited to 6:00 AM or earlier.
        </p>
      </Row>
      <Row>
        <div className="col-7 mt-3">
          <div>
            <input
              id={`enableBusinessHours`}
              type="checkbox"
              className="form-check-input me-2"
              checked={businessHoursEnabled}
              onChange={(ev) => updateSettings({ useBusinessHours: ev.target.checked })}
            />
            <label
              className="form-check-label-lg"
              htmlFor={`enableBusinessHours`}>
              Enable business hours
            </label>
          </div>
          <div className="mt-2" style={getDisabledStyle(!businessHoursEnabled)}>
            <input
              id={`shutOffAccessWhenClosed`}
              type="checkbox"
              className="form-check-input me-2"
              checked={businessHours.shutOffAccessWhenClosed}
              onChange={(ev) => updateSettings({ shutOffAccessWhenClosed: ev.target.checked })}
            />
            <label
              className="form-check-label-lg"
              htmlFor={`shutOffAccessWhenClosed`}>
              Shut off access to the site when closed
            </label>
          </div>
        </div>
        <div className="col-5 d-flex flex-column mt-2" style={getDisabledStyle(!businessHoursEnabled)}>
          <div className="text-end">
            <input
              id={`useLoadoutForReceiving`}
              type="checkbox"
              className="form-check-input me-2"
              checked={businessHours.useLoadoutHoursForReceiving}
              onChange={toggleReceiving} />
            <label
              className="form-check-label-lg"
              htmlFor={`useLoadoutForReceiving`}>
              Use same hours for loadout and receiving
            </label>
          </div>
          <Row className="mt-auto">
            <label className="col-6 col-form-label-lg text-end" htmlFor="timeZone" style={getDisabledStyle(!businessHoursEnabled)}>
              Time Zone
            </label>
            <div className="col-6 bottom-0 pt-1 pb-2" style={getDisabledStyle(!businessHoursEnabled)}>
              <select
                id="timeZone"
                className="form-select form-select-sm mt-1"
                onChange={changeTimeZone}
                value={businessHours.timeZone}>
                <option value={BusinessTimeZone.Eastern}>Eastern Time</option>
                <option value={BusinessTimeZone.Central}>Central Time</option>
                <option value={BusinessTimeZone.Mountain}>Mountain Time</option>
                <option value={BusinessTimeZone.Pacific}>Pacific Time</option>
              </select>
            </div>
          </Row>
        </div>
      </Row>
      {!businessHours.useLoadoutHoursForReceiving &&
        <Nav tabs className="d-flex justify-content-center border-bottom-0 mb-2">
          <NavItem>
            <NavHeader
              enabled={businessHoursEnabled}
              active={!viewReceiving}
              inError={!noErrorsForSaving(Direction.Loadout)}
              text="Loadout"
              onClick={() => setViewReceiving(false)} />
          </NavItem>
          <NavItem>
            <NavHeader
              enabled={businessHoursEnabled}
              active={viewReceiving}
              inError={!noErrorsForSaving(Direction.Receiving)}
              text="Receiving"
              onClick={() => setViewReceiving(true)} />
          </NavItem>
        </Nav>}
      <TabContent activeTab={viewReceiving ? 0 : 1}>
        <div className="border border-dark rounded p-4"
          data-testid="business-hours-container"
          style={getDisabledStyle(!businessHoursEnabled)}>
            {Object.values(DayOfTheWeek).map((day, i) => {
              const direction = viewReceiving ? Direction.Receiving : Direction.Loadout;
              const hours = viewReceiving ? businessHours.receivingHours : businessHours.loadoutHours;
              var timeSpans = getTimeSpans(hours, day);

              if (timeSpans.length === 0) {
                timeSpans = [{ openTime: null, closeTime: null }];
              }
            
              return <BusinessHoursDayEntry
                key={direction.toString() + i}
                day={day}
                timeSpans={timeSpans}
                updateAndValidateHours={(timeSpans: TimeSpan[]) => updateAndValidateHours(direction, day, timeSpans)}
                errors={viewReceiving ? receivingErrors[day] : loadoutErrors[day]} />
            })} 
          </div>
      </TabContent>
    </div>
    <div className="align-self-end mt-3">
      <SpinnerButton
        className="btn btn-primary mx-4 mb-4 px-5 py-3"
        spinning={updateBusinessHoursSettings.isLoading}
        onClick={save}
        disabled={!(noErrorsForSaving(Direction.Loadout) && noErrorsForSaving(Direction.Receiving))}>
        Save
      </SpinnerButton>
    </div>
  </>
}

const BusinessHoursPage = () => {
  const useSettings = useGetSiteSettings<BusinessHoursSettings>();
  return <QueryView
    query={useSettings}
    renderData={settings => <Edit initialBusinessHours={settings.businessHours} />}
  />
};

export default BusinessHoursPage;